import type { BLOCK_TYPE } from '../enums'
import type { ReferenceId } from '../ids'
import type { BlockReference, ContextParameterReference } from '../references'

import type { IBlock, BlockBaseSettings } from './base'

export enum EntitySelectionMode {
  ENTITY_LIST = 'entity-list',
  OTHER_BLOCK = 'other-block',
  CONTEXT = 'context'
}

/**
 * Table Attributes
 */
export interface EntityAttributes {
  /**
   * Attribute ID (UUID) of the related entity
   */
  id: string
  /**
   * Display name of of the attribute
   * Example: Customer ID
   */
  label: string
  /**
   * Key name of the attribute in entity data structure
   * Example: customer_id
   */
  name: string
}

/**
 * Settings
 */
type EntityFinderSettings = {
  /**
   * Whether to show icons in block display
   */
  showIcon: boolean
  /**
   * Whether end users are allow to select multiple items from entity lists
   */
  multiSelect: boolean
  /**
   * Related entity name, entities should be unquiely named in an organization
   * The entity is
   *    configured in ENTITY_LIST mode
   *    passed from another block in OTHER_BLOCK mode
   *    resolved from context parameter (reference Id) in CONTEXT mode
   */
  entity: string
  /**
   * Attributes to show in block display
   */
  attributes: EntityAttributes[]
  /**
   * The title attribute should be references from attributes
   */
  titleAttribute: string
}

/**
 * References
 */
export interface EntityFinderBlockReference extends BlockReference {
  /**
   * Example for @referenceId: 6d8caa5c-4cc6-4caa-8ef4-88c9b341d11b
   */
  meaning: 'source'
}
export interface EntityFinderContextReference
  extends ContextParameterReference {
  /**
   * Example for @referenceId: opportunity_id
   */
  meaning: 'source'
}

/**
 * Blocks
 */
export interface EntityListBasedEntityFinderBlock extends IBlock {
  type: BLOCK_TYPE.ENTITY_FINDER
  settings: BlockBaseSettings &
    EntityFinderSettings & {
      mode: EntitySelectionMode.ENTITY_LIST
    }
  references: {}
}

export interface OtherBlockBasedEntityFinderBlock extends IBlock {
  type: BLOCK_TYPE.ENTITY_FINDER
  settings: BlockBaseSettings &
    EntityFinderSettings & {
      mode: EntitySelectionMode.OTHER_BLOCK
      /**
       * Selected relation from the entity configured in the other block
       * Example: contacts
       */
      relation: string
      /**
       * Slug for selected relation
       * Example: contact
       */
      relationSlug: string
    }
  references: Record<ReferenceId, EntityFinderBlockReference>
}

export interface ContextBasedEntityFinderBlock extends IBlock {
  type: BLOCK_TYPE.ENTITY_FINDER
  settings: BlockBaseSettings &
    EntityFinderSettings & {
      mode: EntitySelectionMode.CONTEXT
      /**
       * Selected relation from the entity configured as context parameter
       * Example: customer
       */
      relation: string
      /**
       * Slug for selected relation
       * Example: contact
       */
      relationSlug: string
    }
  references: Record<ReferenceId, EntityFinderContextReference>
}

export type EntityFinderBlock =
  | EntityListBasedEntityFinderBlock
  | OtherBlockBasedEntityFinderBlock
  | ContextBasedEntityFinderBlock
